import { QuizTopicPageTemplate } from "@/components/QuizTopicPageTemplate";

const tocItems = [
  { id: "why-personality", title: "Why Personality Quizzes?" },
  { id: "self-discovery", title: "Self-Discovery Quizzes" },
  { id: "character-matching", title: "Character Matching" },
  { id: "preference-quizzes", title: "Preference-Based Quizzes" },
];

const quizResources = [
  { name: "16Personalities Test", url: "https://www.16personalities.com/", description: "MBTI-style personality test" },
  { name: "Which Video Game Next?", url: "https://play.howstuffworks.com/quiz/which-video-game-should-you-play-next", description: "Preference-based game recommendations" },
  { name: "Social Media Personality", url: "https://www.proprofs.com/quiz-school/story.php?title=3dq-can-we-guess-which-social-media-you-mostly-visit", description: "Can we guess your social media?" },
  { name: "Friends Trivia Quiz", url: "https://www.seventeen.com/celebrity/celeb-quizzes/a25048028/friends-quiz-trivia/", description: "Which Friends character are you?" },
];

const relatedTopics = [
  { name: "Fun Quizzes", path: "/quizzes/fun-quizzes-to-take/", description: "Entertainment quizzes" },
  { name: "Pop Culture Quizzes", path: "/quizzes/pop-culture-quizzes/", description: "Movies and TV shows" },
  { name: "Trivia Quizzes", path: "/quizzes/trivia-quizzes/", description: "General knowledge" },
];

const PersonalityQuizzes = () => {
  return (
    <QuizTopicPageTemplate
      title="Personality Quizzes: Discover More About Yourself"
      metaTitle="Personality Quizzes | Self-Discovery & Fun Tests 2025"
      metaDescription="Take personality quizzes to discover more about yourself. From MBTI-style tests to 'What type are you?' quizzes - explore your personality in fun, engaging ways."
      keywords="personality quizzes, personality test, what type are you quiz, character quiz, self-discovery quiz, MBTI quiz, buzzfeed personality quiz"
      canonicalUrl="/quizzes/personality-quizzes/"
      category="Self-Discovery"
      publishDate="January 12, 2025"
      readTime="5 min read"
      tocItems={tocItems}
      leadParagraph="Who are you, really? Personality quizzes help you explore your traits, preferences, and quirks in fun and insightful ways. From scientific assessments to lighthearted 'which character are you?' tests, these quizzes satisfy our endless curiosity about ourselves."
      quizResources={quizResources}
      relatedTopics={relatedTopics}
    >
      <section id="why-personality" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Why <span className="text-gradient">Personality Quizzes?</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Personality quizzes tap into our fundamental desire for self-understanding. Whether serious or silly, they give us language to describe who we are and how we relate to others.
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Self-reflection:</strong> Gain insights into your traits and tendencies</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Social sharing:</strong> Compare results with friends</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Entertainment:</strong> Fun way to pass time</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Decision help:</strong> Discover preferences you didn't know you had</span>
          </li>
        </ul>
      </section>

      <section id="self-discovery" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Self-Discovery <span className="text-gradient">Quizzes</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          More serious personality assessments help you understand your psychological type. The <a href="https://www.16personalities.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">16Personalities Test</a> is one of the most popular MBTI-style assessments online.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <a href="https://www.16personalities.com/" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">MBTI-Style Tests</h4>
            <p className="text-sm text-muted-foreground">16 personality types based on preferences</p>
          </a>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Enneagram</h4>
            <p className="text-sm text-muted-foreground">9 interconnected personality types</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Learning Styles</h4>
            <p className="text-sm text-muted-foreground">Visual, auditory, or kinesthetic?</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Career Aptitude</h4>
            <p className="text-sm text-muted-foreground">What jobs suit your personality?</p>
          </div>
        </div>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          These quizzes provide frameworks for understanding yourself better, though they should be taken as guides rather than definitive assessments.
        </p>
      </section>

      <section id="character-matching" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Character <span className="text-gradient">Matching</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          "Which [Character] Are You?" quizzes are endlessly popular because they connect personality to beloved stories and characters:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>TV Show Characters:</strong> <a href="https://www.seventeen.com/celebrity/celeb-quizzes/a25048028/friends-quiz-trivia/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Which Friends character are you?</a> Which Office personality?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Movie Characters:</strong> Which Disney princess? Which Harry Potter house?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Historical Figures:</strong> Which famous leader matches your traits?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Video Game Heroes:</strong> Which game protagonist are you?</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          These quizzes use familiar characters as personality archetypes, making self-discovery fun and relatable.
        </p>
      </section>

      <section id="preference-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Preference-Based <span className="text-gradient">Quizzes</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          These quizzes use your choices and preferences to reveal something about your personality:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>"Build your ideal...":</strong> Create your perfect vacation, home, or day—and discover what it says about you</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>"What should you...?":</strong> <a href="https://play.howstuffworks.com/quiz/which-video-game-should-you-play-next" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Which video game should you play next?</a></span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>"We can guess...?":</strong> <a href="https://www.proprofs.com/quiz-school/story.php?title=3dq-can-we-guess-which-social-media-you-mostly-visit" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Can we guess which social media you use most?</a></span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          HowStuffWorks' <a href="https://play.howstuffworks.com/quiz/which-video-game-should-you-play-next" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Which Video Game Should You Play Next?</a> is a great example—it recommends games based on your personality and gaming preferences.
        </p>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          "What People Like About You" quizzes analyze your traits to reveal your most appealing qualities—great for a confidence boost and self-awareness.
        </p>
      </section>
    </QuizTopicPageTemplate>
  );
};

export default PersonalityQuizzes;
